import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Notification } from '@src/atoms/Notification';
import { Animation } from '@src/core/Animation';
import { Portal } from '@src/core/Portal';
import { TAppNotification, TDevelopmentMode } from '../models';
import { classnames } from '@src/utils/classnames';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { useDebounce } from '@src/hooks/useDebounce';
import { TRANSITION_CLASSES, NOTIFICATION_DELAY_CLOSE } from './consts';
import { Box } from '@src/atoms/Box';
import { $notificationsStore, removeNotification } from '../../../stores';
import { useEvent, useStore } from 'effector-react';
import { Button } from '@src/atoms/Button';
import styled from './AppNotification.scss';

interface IAppNotificationProps extends TAppNotification {}

export const AppNotification: FC<IAppNotificationProps> = ({ mode, content, id, title, view, buttonProps }) => {
  const [open, setOpen] = useState(true);
  const removeNotificationEvent = useEvent(removeNotification);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const closeToast = useDebounce(() => {
    let isAutoClose = true;

    if (
      typeof window !== 'undefined' &&
      mode === 'dev' &&
      typeof window.__CENTRA__?.AUTO_NOTIFICATION_CLOSE === 'boolean'
    ) {
      isAutoClose = window.__CENTRA__.AUTO_NOTIFICATION_CLOSE;
    }

    isAutoClose && close();
  }, NOTIFICATION_DELAY_CLOSE);

  const handleClose = useCallback(() => {
    removeNotificationEvent(id);
  }, [id, removeNotificationEvent]);

  useEffect(() => {
    closeToast();
  }, [closeToast]);

  return (
    <Animation open={open} onExited={handleClose} transitionClasses={TRANSITION_CLASSES}>
      <Notification view={view} title={title} mode="toast" onClose={close}>
        {content}
        {buttonProps && (
          <Box marginTop={{ DESKTOP: 12, MOBILE: 8 }}>
            <Button view="secondaryLight" size="sm" {...buttonProps} id="notificationBtn" />
          </Box>
        )}
      </Notification>
    </Animation>
  );
};

export const AppNotifications: FC<{ mode?: TDevelopmentMode }> = ({ mode }) => {
  const { isMobile } = useContext(BreakpointsContext);
  const notificationList = useStore($notificationsStore);
  const notificationClasses = classnames(styled.root, {
    [styled.rootMobile]: isMobile,
  });

  if (!notificationList.length) return null;

  return (
    <Portal>
      <Box className={notificationClasses} column gap={8} justifyContent="end">
        {notificationList.map((n) => (
          <AppNotification key={n.id} {...n} mode={mode} />
        ))}
      </Box>
    </Portal>
  );
};
